if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.viewport = this.querySelector('[role="document"]');
      this.items = [...this.viewport.children].filter(item => item.dataset.mediaId);
      this.thumbs = [...this.querySelectorAll('[data-viewer-media]')];
      this.pointers = new Map();
      this.scale = 1;
      this.x = this.y = 0;
      this.addEventListener('click', event => {
        const thumb = event.target.closest('[data-viewer-media]');
        if (thumb) this.select(thumb.dataset.viewerMedia);
        const action = event.target.closest('[data-viewer-action]')?.dataset.viewerAction;
        if (action === 'prev') this.step(-1);
        if (action === 'next') this.step(1);
        if (action === 'in') this.zoom(this.scale + .5);
        if (action === 'out') this.zoom(this.scale - .5);
        if (action === 'reset') this.zoom(1);
      });
      this.addEventListener('keydown', event => {
        if (event.target.closest('deferred-media, product-model, input, textarea')) return;
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
        if (event.key === '+' || event.key === '=') this.zoom(this.scale + .5);
        if (event.key === '-') this.zoom(this.scale - .5);
      });
      this.viewport.addEventListener('dblclick', event => {
        if (this.active?.tagName === 'IMG') { event.preventDefault(); this.zoom(this.scale > 1 ? 1 : 2); }
      });
      this.viewport.addEventListener('wheel', event => {
        if (this.active?.tagName !== 'IMG') return;
        event.preventDefault();
        this.zoom(this.scale - event.deltaY * .003);
      }, { passive: false });
      this.viewport.addEventListener('pointerdown', event => this.pointerDown(event));
      this.viewport.addEventListener('pointermove', event => this.pointerMove(event));
      this.viewport.addEventListener('pointerup', event => this.pointerEnd(event));
      this.viewport.addEventListener('pointercancel', event => this.pointerEnd(event, true));
      this.resizeObserver = new ResizeObserver(() => { if (this.hasAttribute('open')) this.fit(); });
    }
    connectedCallback() { super.connectedCallback(); this.resizeObserver.observe(this.viewport); }
    disconnectedCallback() { this.resizeObserver.disconnect(); }
    show(opener) {
      super.show(opener);
      this.select(opener.dataset.mediaId);
    }
    hide() { this.pointers.clear(); this.gesture = null; super.hide(); }
    select(id) {
      window.pauseAllMedia();
      this.active = this.items.find(item => item.dataset.mediaId === String(id)) || this.items[0];
      if (!this.active) return;
      this.items.forEach(item => item.classList.toggle('active', item === this.active));
      this.index = this.thumbs.findIndex(thumb => thumb.dataset.viewerMedia === this.active.dataset.mediaId);
      this.thumbs.forEach((thumb, index) => thumb.setAttribute('aria-current', String(index === this.index)));
      this.querySelector('.memorra-viewer__count').textContent = `${this.index + 1} / ${this.thumbs.length}`;
      this.querySelector('[data-viewer-action="prev"]').disabled = this.index <= 0;
      this.querySelector('[data-viewer-action="next"]').disabled = this.index >= this.thumbs.length - 1;
      const thumb = this.thumbs[this.index];
      if (thumb) thumb.parentElement.scrollTo({ left: thumb.offsetLeft - thumb.parentElement.clientWidth / 2 + thumb.offsetWidth / 2, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      this.scale = 1; this.x = this.y = 0; this.pointers.clear(); this.gesture = null;
      if (this.active.tagName === 'IMG') {
        this.active.draggable = false;
        this.active.onload = () => { if (this.hasAttribute('open')) this.fit(); };
      }
      this.fit();
    }
    step(direction) { const thumb = this.thumbs[this.index + direction]; if (thumb) this.select(thumb.dataset.viewerMedia); }
    fit() {
      if (!this.active) return;
      if (this.active.tagName === 'IMG') {
        const ratio = this.active.naturalWidth / this.active.naturalHeight || Number(this.active.width) / Number(this.active.height) || 1;
        const width = Math.max(1, Math.min(this.viewport.clientWidth, this.viewport.clientHeight * ratio));
        this.baseWidth = width; this.baseHeight = width / ratio;
        this.active.style.width = `${width}px`; this.active.style.height = `${this.baseHeight}px`;
      }
      this.paint();
    }
    zoom(scale) {
      if (this.active?.tagName !== 'IMG') return;
      this.scale = Math.max(1, Math.min(4, scale));
      if (this.scale === 1) this.x = this.y = 0;
      this.paint();
    }
    paint() {
      const image = this.active?.tagName === 'IMG';
      this.toggleAttribute('data-zoomed', image && this.scale > 1);
      if (image) {
        const maxX = Math.max(0, (this.baseWidth * this.scale - this.viewport.clientWidth) / 2);
        const maxY = Math.max(0, (this.baseHeight * this.scale - this.viewport.clientHeight) / 2);
        this.x = Math.max(-maxX, Math.min(maxX, this.x)); this.y = Math.max(-maxY, Math.min(maxY, this.y));
        this.active.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
      }
      this.querySelector('[data-viewer-action="out"]').disabled = !image || this.scale <= 1;
      this.querySelector('[data-viewer-action="in"]').disabled = !image || this.scale >= 4;
      this.querySelector('[data-viewer-action="reset"]').disabled = !image || this.scale <= 1;
    }
    pointerDown(event) {
      if (this.active?.tagName !== 'IMG' || event.button > 0) return;
      this.viewport.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 1) this.gesture = { x: event.clientX, y: event.clientY, panX: this.x, panY: this.y, scale: this.scale, time: Date.now(), multiple: false };
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.gesture.multiple = true;
        this.gesture.distance = Math.hypot(a.x - b.x, a.y - b.y);
        this.gesture.scale = this.scale;
      }
    }
    pointerMove(event) {
      if (!this.pointers.has(event.pointerId) || !this.gesture) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.zoom(this.gesture.scale * Math.hypot(a.x - b.x, a.y - b.y) / Math.max(1, this.gesture.distance));
      } else if (!this.gesture.multiple && this.scale > 1) {
        this.x = this.gesture.panX + event.clientX - this.gesture.x;
        this.y = this.gesture.panY + event.clientY - this.gesture.y;
        this.paint();
      }
    }
    pointerEnd(event, cancelled = false) {
      if (!this.pointers.has(event.pointerId)) return;
      const g = this.gesture;
      this.pointers.delete(event.pointerId);
      if (!cancelled && g && !g.multiple && this.pointers.size === 0) {
        const dx = event.clientX - g.x, dy = event.clientY - g.y;
        if (g.scale === 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.3) this.step(dx < 0 ? 1 : -1);
        else if (event.pointerType === 'touch' && Math.hypot(dx, dy) < 10 && Date.now() - g.time < 300) {
          const now = Date.now();
          if (this.lastTap && now - this.lastTap < 320) { this.zoom(this.scale > 1 ? 1 : 2); this.lastTap = 0; }
          else this.lastTap = now;
        }
      }
      if (!this.pointers.size) this.gesture = null;
    }
  });
}
